// Plan 08-06 — Coach hint registry + match-number gating.
// Plan 14-05 (MUI-05) — extended with findable_word + powerup_timing hints;
// gate switched from $onboarding.matchesPlayed (client tutorial counter) to
// realMatchesPlayed (server-tracked via /api/profile/me — Pitfall 6).
//
// Pure logic only. No React imports — keep this file unit-testable in
// happy-dom without rendering. The CoachOverlay component (CoachOverlay.tsx)
// consumes shouldShowHint + HINT_REGISTRY to decide whether to render.
//
// Range (DEC-027): coach hints render for the first 5 real (post-tutorial)
// matches (ceiling). The server-tracked `users.real_matches_played` counter
// increments on terminal completion of casual/ranked/friend matches (Phase
// 14-05 backend) — practice bot matches do NOT increment, so a player can
// warm up against bots without exhausting their hint budget.

export interface HintCtx {
  /**
   * Phase 14 (MUI-05) — server-tracked counter from /api/profile/me. Drives
   * the < 5 hint ceiling per DEC-027.
   * Decoupled from $onboarding.matchesPlayed (the client tutorial counter)
   * because the two have different semantics (Pitfall 6 in 14-RESEARCH.md).
   */
  realMatchesPlayed: number
  /**
   * Hints already shown in this session (or persisted via $tutorial.hintsShown).
   * Caller should pass `$tutorial.get().hintsShown`.
   */
  hintsShown: ReadonlyArray<string>
  /**
   * @deprecated No longer gates contextual hints. Onboarding clarity requires
   * the live match to teach standalone — a player who SKIPS the tutorial must
   * still get first-use coach hints (the `realMatchesPlayed` ceiling is the
   * veteran suppressor). Kept optional for back-compat with older callers.
   */
  tutorialCompleted?: boolean
}

/**
 * DEC-027 ceiling — coach hints stop firing once the player has finished 5
 * real matches. Below this count, individual hints can still be gated by
 * caller logic (e.g. max 1 per phase per turn).
 */
export const HINT_REAL_MATCH_CEILING = 5

export interface HintRegistryEntry {
  /** i18n copy key (UI-SPEC §Copywriting onboarding.coach.* / coach.*). */
  copy_key: string
  /** Spotlight anchor element used by CoachOverlay's SVG mask cutout. */
  spotlight_target: string
}

/** Plan 07 (i18n) reads this set to verify en + sv translations exist. */
export const HINT_REGISTRY = new Map<string, HintRegistryEntry>([
  [
    'word_trace',
    {
      copy_key: 'onboarding.coach.word_trace',
      spotlight_target: 'seed_runes',
    },
  ],
  [
    'token_bank',
    {
      copy_key: 'onboarding.coach.token_bank',
      spotlight_target: 'token_bank',
    },
  ],
  [
    'first_empower',
    {
      copy_key: 'onboarding.coach.first_empower',
      spotlight_target: 'empower_button',
    },
  ],
  [
    't6_ready',
    {
      copy_key: 'onboarding.coach.t6_ready',
      spotlight_target: 'reveal_word',
    },
  ],
  [
    'king_capture',
    {
      copy_key: 'onboarding.coach.king_capture',
      spotlight_target: 'opponent_king',
    },
  ],
  // Tutorial-step teaching (AFFINITY-01 / ANCHOR-03 / POWERUP-01) — the three
  // systems the scripted tutorial now surfaces via its mock sub-surfaces.
  [
    'affinity_intro',
    {
      copy_key: 'onboarding.coach.affinity_intro',
      spotlight_target: 'affinity_strip',
    },
  ],
  [
    'anchor_intro',
    {
      copy_key: 'onboarding.coach.anchor_intro',
      spotlight_target: 'anchor_cell',
    },
  ],
  [
    'powerup_intro',
    {
      copy_key: 'onboarding.coach.powerup_intro',
      spotlight_target: 'powerup_loadout',
    },
  ],
  // Phase 14-05 (MUI-05) — findable-word outline hint (Phase A). Highlights
  // a server-suggested word path on the board with a Sun-Yellow outline.
  // The cells come from the server-side findable-words list (Phase 13's
  // PlayerView projection); this entry's spotlight_target is the symbolic
  // anchor — the FindableWordOutline component renders the actual cells.
  [
    'findable_word',
    {
      copy_key: 'coach.findable_word',
      spotlight_target: 'board_cell_set',
    },
  ],
  // Phase 14-05 (MUI-05) — power-up timing hint (Phase B). Suggests saving
  // tokens for a clutch turn. Anchored on the Phase B power-up pill.
  [
    'powerup_timing',
    {
      copy_key: 'coach.powerup_timing',
      spotlight_target: 'phase_b_power_pill',
    },
  ],
])

/**
 * Hard-coded English fallback copy. Plan 07 wraps the CoachOverlay in
 * `<FormattedMessage id={entry.copy_key} />` and sources from
 * `apps/web/public/locales/{en,sv}.json`. Until then, the overlay reads from
 * this map so the hint is visible in unit tests + dev builds.
 */
export const HINT_FALLBACK_COPY: Readonly<Record<string, string>> = {
  'onboarding.coach.word_trace': 'Drag across letters to spell.',
  'onboarding.coach.token_bank': 'Tokens build here. Spend them on roles.',
  'onboarding.coach.first_empower': 'Spend a token. Make a Knight.',
  'onboarding.coach.t6_ready': 'Six new runes are about to fall.',
  'onboarding.coach.king_capture': 'Take the King. Win the match.',
  'onboarding.coach.affinity_intro':
    'These four runes are yours alone. Spell with one for +1.',
  'onboarding.coach.anchor_intro':
    'Place an Anchor to brand a cell — +2 for you, but it leaves a Hollow.',
  'onboarding.coach.powerup_intro':
    'You carry four power-ups. Some are used once, others twice.',
  'onboarding.coach.skip': 'Skip',
  // Phase 14-05 (MUI-05) — coach copy fallbacks; en.json carries the
  // canonical i18n strings.
  'coach.findable_word': 'Try this path - orthogonal or diagonal works.',
  'coach.powerup_timing': 'Tokens stacking. Save for a clutch turn.',
}

/**
 * Decide whether to render the coach hint for `key` given the current player
 * context. Pure function — does not subscribe to any store.
 *
 *   - realMatchesPlayed >= ceiling (5) → no hints (experienced player —
 *     DEC-027).
 *   - Hint already shown → no repeats (one-shot per key).
 *
 * Onboarding clarity update: tutorial completion is NO LONGER a gate. The live
 * match must be self-explanatory even for a player who skips the tutorial, so
 * contextual first-use hints fire for any newcomer (realMatchesPlayed < 5)
 * regardless of whether they completed or skipped the tutorial. The
 * server-tracked `realMatchesPlayed` ceiling is the single veteran suppressor
 * (Pitfall 6: a tutorial REPLAY can't re-enable hints because it doesn't touch
 * the server counter).
 */
export function shouldShowHint(key: string, ctx: HintCtx): boolean {
  if (ctx.realMatchesPlayed >= HINT_REAL_MATCH_CEILING) return false
  if (ctx.hintsShown.includes(key)) return false
  return true
}
